import { Controller } from "@hotwired/stimulus"
import { debounce } from "lodash"

export default class extends Controller {
  static targets = ["bulkList", "moreDropdown", "countInfo", "totalCount", "moreBtn", "record", "optionsMenu", "reverseOptionsMenu", "tableContainer",
    "blockedElem"]

  static outlets = ["summation"]
  static values = {
    tableHoverClass: String,
    tableHideClass: String,
    translations: Object
  }

  selectedRecords = []
  visibleBulkWidth = 0

  connect() {
    // referencja trzymana w polu, bo .bind() zwraca za każdym wywołaniem nową funkcję
    // i removeEventListener nie trafiłby w listener dodany tutaj
    this.debouncedUpdateMenu = debounce(this.updateMenu.bind(this), 150)
    window.addEventListener("resize", this.debouncedUpdateMenu)
  }

  disconnect() {
    window.removeEventListener("resize", this.debouncedUpdateMenu)
    this.debouncedUpdateMenu.cancel()
  }

  bulkSwitch() {
    this.selectedRecords = this.recordTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)
    const hasSelectedRecords = this.selectedRecords.length > 0

    if (this.hasOptionsMenuTarget) {
      this.optionsMenuTarget.classList.toggle("hidden", !hasSelectedRecords)
      if (hasSelectedRecords) {
        this.updateMenu()
      }
    }
    if (this.hasReverseOptionsMenuTarget) {
      this.reverseOptionsMenuTarget.classList.toggle("hidden", hasSelectedRecords)
    }

    this.updateCountInfo()
    if (this.hasSummationOutlet) {
      this.summationOutlet.updateTotals(this.selectedRecords)
    }

    window.FIRMLET_BULK_IDS = this.selectedRecords //TODO usunac po przepisaniu reszty js
    this.toggleTableMargin(hasSelectedRecords)
  }

  toggleTableMargin(isVisible) {
    if (this.hasTableContainerTarget) {
      this.tableContainerTarget.classList.toggle(this.tableHoverClassValue, isVisible)
      this.tableContainerTarget.classList.toggle(this.tableHideClassValue, !isVisible)
    }
  }

  bulkSelectSwitch(event) {
    const checkedAll = event.currentTarget.checked

    this.recordTargets.forEach(record => {
      record.checked = checkedAll
    })

    this.bulkSwitch()
  }

  updateCountInfo() {
    const translationKey = this.selectedRecords.length === 1 ? "one" : (this.selectedRecords.length < 5 ? "few" : "other")
    this.countInfoTarget.innerHTML = this.translationsValue.countInfo[translationKey].replace("%{count}", this.selectedRecords.length)
    if (this.hasTotalCountTarget) {
      this.totalCountTarget.innerHTML = this.selectedRecords.length > 0 ? this.selectedRecords.length : this.recordTargets.length
    }
  }

  // TODO: duplikat overflow-logiki z toolbar_overflow_controller.js (wydzielonego dla statycznego
  // paska w warehouse_documents/_show_buttons). Docelowo przenieść ten widok i pozostałe (products,
  // clients, banking/payments, warehouse_documents index, accounting_office/companies) na
  // toolbar-overflow + osobny mechanizm zaznaczania, i usunąć tę metodę — osobny PR.
  updateMenu() {
    const list = this.bulkListTarget
    const dropdownContainer = this.moreDropdownTarget
    const menuContainerWidth = list.parentElement.offsetWidth

    if (this.visibleBulkWidth === menuContainerWidth) return

    // reset PRZED pomiarem blockedElem — dzięki temu blockedElem.offsetWidth
    // nigdy nie obejmuje (schowanego) moreBtn
    this.moreBtnTarget.classList.add("hidden")
    Array.from(dropdownContainer.children).forEach((item) => {
      this.updateSelectState(item, "destroy")
      const clone = this.cloneWithEvents(item)
      item.remove()
      list.appendChild(clone)
      this.updateSelectState(clone)
    })

    const reservedWidth = (this.hasCountInfoTarget ? this.countInfoTarget.offsetWidth : 0) +
      (this.hasBlockedElemTarget ? this.blockedElemTarget.offsetWidth : 0)
    const availableWidth = menuContainerWidth - reservedWidth

    let totalWidth = 0
    Array.from(list.children).forEach((item) => { totalWidth += item.offsetWidth })

    if (totalWidth > availableWidth) {
      this.moreBtnTarget.classList.remove("hidden")
      const limit = availableWidth - this.moreBtnTarget.offsetWidth
      let used = 0
      Array.from(list.children).forEach((item) => {
        used += item.offsetWidth
        if (used > limit) {
          this.updateSelectState(item, "destroy")
          const clone = this.cloneWithEvents(item)
          item.remove()
          dropdownContainer.appendChild(clone)
          this.updateSelectState(clone)
        }
      })
    }

    this.visibleBulkWidth = menuContainerWidth
  }

  cloneWithEvents(src, events = ["onclick", "onmouseover"]) {
    const clone = src.cloneNode(true)

    function copyEvents(origEl, cloneEl) {
      events.forEach(ev => {
        if (origEl[ev]) cloneEl[ev] = origEl[ev]
      })

      Array.from(origEl.children).forEach((child, i) => {
        copyEvents(child, cloneEl.children[i])
      })
    }

    copyEvents(src, clone)
    return clone
  }

  // Masowa operacja REST (PATCH/DELETE/POST /invoices/bulk_*) — wysyła zaznaczone faktury
  // przez ukryty #bulk_action_form (wzorzec za submitBulkKsefSend). Paramy z data-*-param:
  //   data-list--bulk-url-param          → URL endpointu (np. /invoices/bulk_status)
  //   data-list--bulk-http-method-param  → czasownik HTTP (patch/delete/post), domyślnie patch
  //   data-list--bulk-fields-param       → dodatkowe paramy operacji jako JSON (np. {"status":"paid"})
  //   data-list--bulk-confirm-param      → opcjonalny komunikat potwierdzenia (window.confirm)
  submitBulk(event) {
    event.preventDefault()
    const { url, httpMethod, fields, confirm } = event.params
    if (confirm && !window.confirm(confirm)) return
    this.submitBulkForIds(this.selectedRecords, { url, httpMethod, fields })
  }

  // Wariant submitBulk z jawną listą invoice_ids (np. masowe usuwanie wybiera podzbiór
  // zaznaczonych faktur — invoice_bulk_delete_controller liczy które wolno usunąć).
  submitBulkForIds(ids, { url, httpMethod, fields }) {
    const form = document.getElementById("bulk_action_form")
    if (!form) {
      console.error("[bulk_action] #bulk_action_form not in DOM")
      return
    }

    form.querySelectorAll("[data-bulk-dynamic]").forEach(el => el.remove())

    for (const id of ids) {
      this.appendBulkInput(form, "invoice_ids[]", id)
    }
    for (const [name, value] of Object.entries(fields || {})) {
      this.appendBulkInput(form, name, value)
    }

    form.action = url
    const methodInput = form.querySelector('input[name="_method"]')
    if (methodInput) methodInput.value = httpMethod || "patch"

    form.submit()
  }

  appendBulkInput(form, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = String(value)
    input.dataset.bulkDynamic = ""
    form.appendChild(input)
  }

  // Czysta nawigacja GET z zaznaczonymi fakturami (bez mutacji, bez round-tripu przez #index).
  // Buduje URL z paramem tablicowym (np. from_invoice[]=… / from_invoiceable[]=…) i przechodzi
  // window.location. Paramy z data-*-param:
  //   data-list--bulk-url-param   → bazowy URL (np. /warehouse_documents/new)
  //   data-list--bulk-param-param → nazwa parametru tablicowego (from_invoice / from_invoiceable)
  navigate(event) {
    event.preventDefault()
    const { url, param } = event.params
    const query = this.selectedRecords
      .map(id => `${param}[]=${encodeURIComponent(id)}`)
      .join("&")
    const separator = url.includes("?") ? "&" : "?"
    window.location = query ? `${url}${separator}${query}` : url
  }

  //drut dla select2 do usuniecia po wymianie select2
  updateSelectState(item, action = "init") {
    if (item.hasAttribute("data-with-select")) {
      if (action === "init") {
        init_select()
      } else if (action === "destroy") {
        destroy_select()
      }
    }
  }
}
